import { useState, useEffect } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';

type FilterState = Record<string, string[]>;

export function useFilterState() {
  // State for filter management
  const [filters, setFilters] = useState<FilterState>({});
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  // Initialize filters from URL only - no longer using localStorage for initial load
  useEffect(() => {
    const knownFilterKeys = ['sentiment', 'category', 'brandName', 'source', 'visibility', 'startDate', 'endDate', 'id'];
    const urlParams = new URLSearchParams(searchParams.toString()); // Create from current params
    const newFilters: FilterState = {};
    let hasUrlFilters = false;

    // Populate newFilters from URL params
    knownFilterKeys.forEach(key => {
      if (urlParams.has(key)) {
        const value = urlParams.get(key);
        if (value) { 
          // Split comma-separated values into arrays for multi-select filters
          if (key === 'startDate' || key === 'endDate') {
            // Date fields should be single values
            newFilters[key] = [value];
          } else {
            // Other fields can be comma-separated multi-select
            newFilters[key] = value.split(',').map(v => v.trim()).filter(v => v.length > 0);
          }
          hasUrlFilters = true;
        }
      }
    });

    if (hasUrlFilters) {
      // If URL has filters, use them
      console.log('[useFilterState] Initializing filters from URL:', JSON.stringify(newFilters));
      setFilters(newFilters);
    } else {
      // If no relevant URL filters are found, start with empty filters
      console.log('[useFilterState] No URL filters detected, starting with empty filters.');
      setFilters({});
    }
    
    setIsLoadingFilters(false);
  }, [searchParams]); // Dependency remains searchParams

  // Update filters without persisting to localStorage
  const updateFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    updateUrlWithFilters(newFilters);
  };

  // Handle filter changes
  const handleFilterChange = (field: string, value: string, checked: boolean) => {
    const updatedFilters = { ...filters };
    const currentValues = updatedFilters[field] || [];
    
    if (checked) {
      // Add value to filter
      updatedFilters[field] = [...currentValues, value];
    } else {
      // Remove value from filter
      updatedFilters[field] = currentValues.filter(v => v !== value);
      
      // Remove the field entirely if empty
      if (updatedFilters[field].length === 0) {
        delete updatedFilters[field];
      }
    }
    
    updateFilters(updatedFilters);
  };

  // Clear a specific filter
  const clearFilter = (field: string) => {
    const updatedFilters = { ...filters };
    delete updatedFilters[field];
    updateFilters(updatedFilters);
  };

  // Clear all filters
  const clearAllFilters = () => {
    // Clear all filters completely
    setFilters({});
    
    // Update URL to remove all filter parameters
    window.history.replaceState(null, '', pathname);
  };

  // Update URL without causing a page refresh
  const updateUrlWithFilters = (filtersToApply: FilterState) => {
    const params = new URLSearchParams();
    
    // Add each filter to URL params
    Object.entries(filtersToApply).forEach(([field, values]) => {
      if (values && values.length > 0) {
        if (field === 'startDate' || field === 'endDate') {
          // Date fields should be single values
          params.set(field, values[0]);
        } else {
          // Other fields should be comma-separated for multiple values
          params.set(field, values.join(','));
        }
      }
    });
    
    const queryString = params.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
    window.history.replaceState(null, '', newUrl);
  };

  return {
    filters,
    isLoadingFilters,
    handleFilterChange,
    clearFilter,
    clearAllFilters,
    updateFilters,
    setFilters
  };
} 